import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import { DurationHmsField } from '../molecues/DurationHmsField';
import type { Episode } from '../../types/api/episode';

export interface EpisodeFormValues {
  seasonNumber: number;
  episodeNumber: number;
  title: string;
  description: string;
  releaseDate: string;
  duration: number;
  thumbnailUrl?: string;
  thumbnailFile?: File | null;
}

export interface EpisodeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: EpisodeFormValues) => void | Promise<void>;
  mode: 'create' | 'edit';
  episode?: Episode;
  defaultSeason?: number;
  defaultDuration?: number;
}

export const EpisodeFormModal: React.FC<EpisodeFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  episode,
  defaultSeason,
  defaultDuration,
}) => {
  const [formData, setFormData] = useState<EpisodeFormValues>({
    seasonNumber: defaultSeason ?? 1,
    episodeNumber: 1,
    title: '',
    description: '',
    releaseDate: '',
    duration: defaultDuration ?? 2700,
    thumbnailUrl: '',
    thumbnailFile: null,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [thumbnailPreview, setThumbnailPreview] = useState<string | null>(null);

  useEffect(() => {
    if (episode && mode === 'edit') {
      setFormData({
        seasonNumber: episode.seasonNumber,
        episodeNumber: episode.episodeNumber,
        title: episode.title,
        description: episode.description,
        releaseDate: episode.releaseDate,
        duration: episode.duration,
        thumbnailUrl: episode.thumbnailUrl || '',
        thumbnailFile: null,
      });
      setThumbnailPreview(episode.thumbnailUrl || null);
    } else {
      setFormData({
        seasonNumber: defaultSeason ?? 1,
        episodeNumber: 1,
        title: '',
        description: '',
        releaseDate: '',
        duration: defaultDuration ?? 2700,
        thumbnailUrl: '',
        thumbnailFile: null,
      });
      setThumbnailPreview(null);
    }
    setErrors({});
  }, [episode, mode, isOpen, defaultSeason, defaultDuration]);

  const handleChange = (field: keyof EpisodeFormValues, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.seasonNumber || formData.seasonNumber < 1) {
      newErrors.seasonNumber = 'Season number must be at least 1';
    }
    if (!formData.episodeNumber || formData.episodeNumber < 1) {
      newErrors.episodeNumber = 'Episode number must be at least 1';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }
    if (!formData.releaseDate) {
      newErrors.releaseDate = 'Release date is required';
    }
    if (!formData.duration || formData.duration < 1) {
      newErrors.duration = 'Duration must be at least 1 second';
    }
    if (mode === 'create' && !formData.thumbnailFile) {
      newErrors.thumbnailUrl = 'Thumbnail image is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setIsSubmitting(true);
    try {
      await onSubmit(formData);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleThumbnailChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    if (!file) {
      setFormData((prev) => ({ ...prev, thumbnailFile: null }));
      setThumbnailPreview(null);
      return;
    }

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({
        ...prev,
        thumbnailUrl: 'Please select an image file',
      }));
      setFormData((prev) => ({ ...prev, thumbnailFile: null }));
      setThumbnailPreview(null);
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        thumbnailUrl: 'File size must be less than 10MB',
      }));
      setFormData((prev) => ({ ...prev, thumbnailFile: null }));
      setThumbnailPreview(null);
      return;
    }

    setFormData((prev) => ({ ...prev, thumbnailFile: file }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.thumbnailUrl;
      return next;
    });

    const reader = new FileReader();
    reader.onloadend = () => {
      setThumbnailPreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, []);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Add Episode' : 'Edit Episode'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            type="submit"
          >
            {mode === 'create' ? 'Create Episode' : 'Update Episode'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Season Number"
            type="number"
            value={formData.seasonNumber.toString()}
            onChange={(e) =>
              handleChange('seasonNumber', parseInt(e.target.value, 10) || 1)
            }
            error={errors.seasonNumber}
            required
            min={1}
            placeholder="1"
          />
          <FormField
            label="Episode Number"
            type="number"
            value={formData.episodeNumber.toString()}
            onChange={(e) =>
              handleChange('episodeNumber', parseInt(e.target.value, 10) || 1)
            }
            error={errors.episodeNumber}
            required
            min={1}
            placeholder="1"
          />
        </div>

        <FormField
          label="Title"
          value={formData.title}
          onChange={(e) => handleChange('title', e.target.value)}
          error={errors.title}
          required
          placeholder="Enter episode title"
        />

        <div className="w-full">
          <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
            Description <span className="text-red-500">*</span>
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => handleChange('description', e.target.value)}
            placeholder="Enter episode synopsis"
            rows={3}
            className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
          />
          {errors.description && (
            <p className="text-sm text-red-600 mt-1.5">{errors.description}</p>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Release Date"
            type="date"
            value={formData.releaseDate}
            onChange={(e) => handleChange('releaseDate', e.target.value)}
            error={errors.releaseDate}
            required
          />
          <DurationHmsField
            label="Duration"
            valueSeconds={formData.duration}
            onSecondsChange={(sec) => handleChange('duration', sec ?? 0)}
            required
            error={errors.duration}
            helperText="Stored and sent as total seconds."
            idPrefix="episode-modal-duration"
          />
        </div>
        
        {/* Thumbnail upload (create only) */}
        {mode === 'create' && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
              Thumbnail Image <span className="text-red-500">*</span>
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailChange}
              className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-base focus:border-[#EAB308] focus:ring-2 focus:ring-[#EAB308]/20 transition-all duration-200 focus:outline-none"
            />
            {errors.thumbnailUrl && (
              <p className="text-sm text-red-600 mt-1.5">{errors.thumbnailUrl}</p>
            )}
            <p className="text-sm text-gray-500 mt-1.5">
              JPEG, PNG, or WebP. Max 10MB. Image will be uploaded and stored with this episode.
            </p>
            {thumbnailPreview && (
              <div className="mt-3">
                <img
                  src={thumbnailPreview}
                  alt="Thumbnail preview"
                  className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                />
              </div>
            )}
          </div>
        )}

        {/* Thumbnail URL (edit mode) */}
        {mode === 'edit' && (
          <FormField
            label="Thumbnail URL"
            value={formData.thumbnailUrl || ''}
            onChange={(e) => handleChange('thumbnailUrl', e.target.value)}
            error={errors.thumbnailUrl}
            placeholder="https://cdn.example.com/episode-thumbnail.jpg"
            helperText="Optional: update the episode thumbnail URL."
          />
        )}
      </form>
    </Modal>
  );
};


